import { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  getActivoPorId,
  getFacturasParaVincularActivo,
  getActivosParaVincularFactura,
  vincularFacturaActivo,
} from "../../utils/activos";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import ActivosCandidatosLista from "./ActivosCandidatosLista";

// Fase 1c (proyección/factura/pago, 2026-10-04) — molde VincularFacturaCostoModal (Cartelería Fase
// 4): vincula una factura SII recibida con 1..N activos planificados de Edificios, vía la RPC
// `vincular_factura_activo` (única escritura — rama Edificios SOLAMENTE, la rama Cartelería
// (costos_proyecto/vincular_documento_costo) queda para la Fase 1d, sin tocar acá).
//
// Dos entry points, mismo modal (shell compartido, decisión 2026-10-03):
//   - origen="activo": desde una fila de Proyectos ("Ya está facturado") — el activo ya se conoce,
//     se elige UNA factura candidata de su proveedor.
//   - origen="factura": desde una fila de Por pagar ("Corresponde a una proyección") — la factura ya
//     se conoce, se eligen 1+ activos planificados candidatos de su proveedor.
//
// La reconciliación de monto ("la factura manda", sin umbral de bloqueo) la hace la RPC — este
// modal solo muestra la diferencia ANTES de confirmar, para que el usuario no se sorprenda.
// La lista de candidatos (descripción/edificio/monto/estado, agrupada por proveedor) vive en
// `ActivosCandidatosLista` (Fase 2e) — reusada también por el freno anti-duplicado de
// `AsignarRecibidaModal` (10.1).

const VincularProyeccionFacturaModal = ({
  show,
  onHide,
  origen, // "activo" | "factura"
  activoId, // origen="activo"
  facturaId, // origen="factura"
  proveedorId, // origen="factura" (de la fila de Por pagar)
  pais,
  userId,
  onVinculado, // (activoIds: number[]) => void
}) => {
  const [cargando, setCargando] = useState(true);
  const [activo, setActivo] = useState(null); // origen="activo"
  const [factura, setFactura] = useState(null); // origen="factura" (self-fetch)
  const [facturasCandidatas, setFacturasCandidatas] = useState([]); // origen="activo"
  const [activosCandidatos, setActivosCandidatos] = useState([]); // origen="factura"
  const [facturaSeleccionadaId, setFacturaSeleccionadaId] = useState(""); // origen="activo"
  const [activosSeleccionados, setActivosSeleccionados] = useState(() => new Set()); // origen="factura"
  const [guardando, setGuardando] = useState(false);

  const { formatAmount } = useCurrency();

  useEffect(() => {
    if (!show) return;
    let vivo = true;
    (async () => {
      setCargando(true);
      try {
        if (origen === "activo") {
          const a = await getActivoPorId(activoId);
          if (!vivo) return;
          setActivo(a);
          const candidatas = await getFacturasParaVincularActivo({ pais: a.pais, proveedorId: a.proveedor_id });
          if (!vivo) return;
          setFacturasCandidatas(candidatas);
        } else {
          const { data: f, error } = await client
            .from("facturas_sii_recibidas")
            .select("id, folio, fecha_emision, monto_neto, proveedor_id, pais")
            .eq("id", facturaId)
            .single();
          if (error) throw error;
          if (!vivo) return;
          setFactura(f);
          const candidatos = await getActivosParaVincularFactura({ pais: f.pais, proveedorId: f.proveedor_id });
          if (!vivo) return;
          setActivosCandidatos(candidatos);
        }
      } catch (e) {
        toast.error(`Error al cargar candidatos: ${e.message || e}`);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, origen, activoId, facturaId]);

  const facturaSeleccionada = useMemo(
    () => facturasCandidatas.find((f) => String(f.id) === facturaSeleccionadaId) || null,
    [facturasCandidatas, facturaSeleccionadaId]
  );

  const toggleActivo = (id) => {
    setActivosSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Diferencia de monto a mostrar ANTES de confirmar — la RPC reconcilia igual sin preguntar, esto
  // es solo para que el usuario no se sorprenda con el costo_unitario que va a quedar pisado.
  const { sumaActivos, montoFactura, diferencia } = useMemo(() => {
    if (origen === "activo") {
      const suma = Number(activo?.costo_unitario || 0);
      const monto = facturaSeleccionada ? Number(facturaSeleccionada.montoNeto) : null;
      return { sumaActivos: suma, montoFactura: monto, diferencia: monto != null ? suma - monto : null };
    }
    const seleccionados = activosCandidatos.filter((a) => activosSeleccionados.has(a.id));
    const suma = seleccionados.reduce((acc, a) => acc + Number(a.costo_unitario || 0), 0);
    const monto = factura ? Number(factura.monto_neto) : null;
    return { sumaActivos: suma, montoFactura: monto, diferencia: monto != null ? suma - monto : null };
  }, [origen, activo, facturaSeleccionada, activosCandidatos, activosSeleccionados, factura]);

  const monedaActivo = origen === "activo" ? activo?.moneda || "CLP" : "CLP";
  const puedeConfirmar = origen === "activo" ? !!facturaSeleccionada : activosSeleccionados.size > 0 && !!factura;

  const handleGuardar = async () => {
    if (!puedeConfirmar) return;
    setGuardando(true);
    try {
      const fId = origen === "activo" ? facturaSeleccionada.id : factura.id;
      const activoIds =
        origen === "activo"
          ? [activo.id]
          : activosCandidatos.filter((a) => activosSeleccionados.has(a.id)).map((a) => a.id);
      await vincularFacturaActivo({
        facturaId: fId,
        activoIds,
        usuarioId: Number.isFinite(Number(userId)) ? Number(userId) : null,
      });
      toast.success(activoIds.length > 1 ? `${activoIds.length} activos vinculados` : "Activo vinculado a la factura");
      onVinculado?.(activoIds);
    } catch (err) {
      // 10.177: el mensaje real de la RPC (candados de proveedor/país/candidato), tal cual.
      toast.error("Error al vincular: " + (err.message || err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{origen === "activo" ? "Ya está facturado" : "Corresponde a una proyección"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {cargando ? (
          <div style={{ textAlign: "center", padding: 20 }}>Cargando…</div>
        ) : origen === "activo" ? (
          <>
            <div style={{ fontSize: "0.8rem", color: "#6c757d", marginBottom: 12 }}>
              Activo: <strong>{activo?.descripcion || `#${activo?.id}`}</strong> — costo planificado{" "}
              <strong>{formatAmount(activo?.costo_unitario, monedaActivo)}</strong>. Solo se listan facturas SII
              recibidas de este proveedor, sin asociación vigente.
            </div>

            {facturasCandidatas.length === 0 ? (
              <div style={{ color: "#999", textAlign: "center", padding: 20 }}>
                No hay facturas candidatas de este proveedor.
              </div>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Factura</Form.Label>
                <Form.Select value={facturaSeleccionadaId} onChange={(e) => setFacturaSeleccionadaId(e.target.value)}>
                  <option value="">Seleccioná una factura…</option>
                  {facturasCandidatas.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label} — {f.fechaEmision || "s/fecha"} — neto {formatAmount(f.montoNeto, "CLP")}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
          </>
        ) : (
          <>
            <div style={{ fontSize: "0.8rem", color: "#6c757d", marginBottom: 12 }}>
              Factura: <strong>Folio {factura?.folio}</strong> — neto{" "}
              <strong>{formatAmount(factura?.monto_neto, "CLP")}</strong>. Elegí 1 o más activos planificados de
              Edificios que correspondan a esta factura.
            </div>

            <ActivosCandidatosLista
              activos={activosCandidatos}
              seleccionados={activosSeleccionados}
              onToggle={toggleActivo}
            />
          </>
        )}

        {montoFactura != null && (
          <div
            style={{
              marginTop: 8,
              padding: "8px 12px",
              borderRadius: 6,
              fontSize: "0.82rem",
              background: Math.abs(diferencia) > 1 ? "#fff3cd" : "#e8f5e9",
              border: `1px solid ${Math.abs(diferencia) > 1 ? "#f0d878" : "#a5d6a7"}`,
              color: Math.abs(diferencia) > 1 ? "#856404" : "#2e7d32",
            }}
          >
            Σ activos {formatAmount(sumaActivos, "CLP")} vs. factura {formatAmount(montoFactura, "CLP")}
            {Math.abs(diferencia) > 1 ? (
              <>
                {" "}
                — diferencia {formatAmount(Math.abs(diferencia), "CLP")}. La factura manda: el costo del activo se va a
                ajustar a lo facturado.
              </>
            ) : (
              <> — coinciden, sin ajuste.</>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={guardando}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleGuardar} disabled={guardando || !puedeConfirmar}>
          {guardando ? "Vinculando…" : "Vincular"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VincularProyeccionFacturaModal;
